import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

// ---------------------------------------------------------------------------
// Tier constants (mirrored from shared-types/tier.types.ts — not importable
// in the Angular browser bundle because shared-utils depends on Node.js APIs)
// ---------------------------------------------------------------------------

/** The lookback vocabulary an unknown tier resolves to — STARTER's, which is also the floor. */
const FALLBACK_LOOKBACK_DAYS: readonly number[] = [7, 14];

/** The range a page opens on whenever the tier's vocabulary offers it (see `lookbackDefault`). */
const DEFAULT_LOOKBACK_DAYS = 30;

// Finite caps — mirrors the website tier table (2026-09-07) and the backend
// TIER_LIMITS in tier.service.ts. There is no "unlimited" tier anywhere.
// `lookbackDays` is NOT a cap but the range VOCABULARY, mirroring the backend
// `getLookbackDays(tier)` (`LOOKBACK_DAYS_BY_TIER` in tier.service.ts). The two must stay
// identical: a range offered here that seller-api does not recognise is clamped server-side,
// so the page would silently show a different window than the seller picked.
const TIER_LIMITS: Record<string, { maxMarketplaces: number; maxMonthlyOrders: number; maxCampaigns: number; maxFbmOrders: number; readonly lookbackDays: readonly number[] }> = {
  STARTER: { maxMarketplaces: 1, maxMonthlyOrders: 300, maxCampaigns: 2, maxFbmOrders: 50, lookbackDays: FALLBACK_LOOKBACK_DAYS },
  GROWTH:  { maxMarketplaces: 2, maxMonthlyOrders: 1500, maxCampaigns: 10, maxFbmOrders: 500, lookbackDays: [7, 14, 30, 60] },
  PRO:     { maxMarketplaces: 6, maxMonthlyOrders: 4500, maxCampaigns: 30, maxFbmOrders: 1500, lookbackDays: [7, 14, 30, 45, 60, 90] },
};

const AGENT_TIER_LABELS: Record<string, string> = {
  STARTER: 'Standard',
  GROWTH:  'Advanced',
  PRO:     'Strategic',
};

const AI_BUDGETS: Record<string, number> = {
  STARTER: 3.50,
  GROWTH:  9.00,
  PRO:     20.00,
};

function extractBaseTier(planTier: string): string {
  return planTier.replace(/_(USD|INR)$/, '');
}

// ---------------------------------------------------------------------------
// Local interfaces
// ---------------------------------------------------------------------------

interface SellerUserConfig {
  readonly billing_cycle?: string;
  readonly billing_cycle_reset_date?: string;
  readonly monthly_order_count?: number;
  readonly monthly_fbm_order_count?: number;
  readonly monthly_ai_cost_usd?: number;
  readonly ai_budget_exhausted?: boolean;
  readonly managed_campaign_ids?: readonly string[];
  readonly selected_marketplaces?: readonly string[];
  readonly orders_paused?: boolean;
}

/** A single marketplace entry as surfaced by GET /auth/me. */
interface MarketplaceInfo {
  readonly marketplace_id: string;
  readonly country: string;
  readonly is_primary: boolean;
}

interface SellerUser {
  readonly tenantId: string;
  readonly email: string;
  readonly planTier: string;
  /**
   * V36 (2026-10-04) — the tier every LIMIT resolves through, sent by /auth/me
   * (`tierService.limitTierOf`, EC-29). Differs from `planTier` for an ACTIVE trial:
   * planTier stays `PRO_*` (features) while this is `GROWTH_*` (allowance). Optional so a
   * response predating the field still maps (the mirror falls back to planTier).
   */
  readonly limitTier?: string;
  readonly subscriptionStatus: string;
  readonly trialExpiresAt?: string;
  readonly currency: 'USD' | 'INR';
  readonly marketplaces: readonly MarketplaceInfo[];
  readonly notificationsConfigured: boolean;
  readonly notificationChannel: 'telegram' | 'whatsapp';
  readonly budgetExhausted: boolean;
  readonly byokRecommended: boolean;
  readonly isPmk: boolean;
  readonly config: SellerUserConfig;
}

// Shape returned by api-gateway POST /auth/login
interface GatewayTokenResponse {
  readonly access_token: string;
  readonly token_type: string;
  readonly expires_in: number;
}

// Shape returned by api-gateway GET /auth/me (seller token).
// marketplaces is an array of MarketplaceConfig objects from DynamoDB,
// NOT a flat string array. We extract .marketplace_id, .country, and
// .is_primary when mapping to SellerUser.
interface GatewayMeResponse {
  readonly tenant_id: string;
  readonly email: string;
  readonly plan_tier: string;
  /** V36 — the resolved LIMIT tier (`tierService.limitTierOf`); see SellerUser.limitTier. */
  readonly limit_tier?: string;
  readonly currency: 'USD' | 'INR';
  readonly subscription_status: string;
  readonly trial_expires_at?: string;
  readonly marketplaces: readonly {
    readonly marketplace_id: string;
    readonly country: string;
    readonly is_primary: boolean;
  }[];
  readonly is_pmk: boolean;
  readonly notification_channel: 'telegram' | 'whatsapp';
  readonly notifications_configured: boolean;
  readonly access_token?: string;
  readonly budget_exhausted?: boolean;
  readonly byok_recommended?: boolean;
  readonly config?: {
    readonly billing_cycle?: string;
    readonly billing_cycle_reset_date?: string;
    readonly monthly_order_count?: number;
    readonly monthly_fbm_order_count?: number;
    readonly monthly_ai_cost_usd?: number;
    readonly ai_budget_exhausted?: boolean;
    readonly managed_campaign_ids?: readonly string[];
    readonly selected_marketplaces?: readonly string[];
    readonly orders_paused?: boolean;
  };
}

/** Shape returned by GET /tenant/usage (seller-api). Mirrors shared-types TenantUsage. */
interface TenantUsage {
  readonly tenant_id?: string;
  readonly plan_tier?: string;
  readonly monthly_order_count: number;
  readonly max_monthly_orders: number;
  readonly monthly_fbm_order_count: number;
  readonly max_fbm_orders: number;
  readonly managed_campaigns: number;
  readonly max_campaigns: number;
  readonly monthly_ai_cost_usd: number;
  readonly ai_budget_usd: number;
  readonly orders_paused: boolean;
  readonly ai_budget_exhausted: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly TOKEN_KEY = 'ss_seller_token';

  private _user = signal<SellerUser | null>(null);
  private _initialized = signal(false);

  // In production (same-origin, HTTPS): the HttpOnly ss_token cookie handles all
  // auth. sessionStorage is NOT used — XSS-safe, same as before this change.
  //
  // In staging/dev (cross-origin, HTTP localhost): the cookie can't be set
  // (Secure + SameSite=Strict). We persist the Bearer token to sessionStorage
  // so auth survives page refresh. Cleared on tab close.
  private _token = signal<string | null>(
    environment.production ? null : sessionStorage.getItem(this.TOKEN_KEY),
  );

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly isInitialized = this._initialized.asReadonly();
  readonly planTier = computed(() => this._user()?.planTier ?? '');
  /**
   * The base tier every LIMIT in this mirror resolves through (V36, 2026-10-04) — the
   * SERVER's own `tierService.limitTierOf(tenant)`, sent as `limit_tier`. **Use this for
   * limits; use `planTier()` only for FEATURE gating** (nav locks, listing readiness,
   * brand analytics, review insights, WhatsApp availability): a trial is provisioned
   * `PRO_*` so every feature stays available while the allowance is Growth's (EC-29).
   * Falls back to the plan tier for a response predating the field — never re-derive the
   * trial rule here; a second copy is the defect EC-29 exists to prevent.
   */
  readonly limitTier = computed(() => {
    const tier = this._user()?.limitTier ?? this._user()?.planTier ?? '';
    return tier ? extractBaseTier(tier) : '';
  });
  readonly currency = computed(() => this._user()?.currency ?? 'USD');
  readonly marketplaces = computed(() => this._user()?.marketplaces ?? []);
  readonly primaryMarketplace = computed(() =>
    this._user()?.marketplaces?.find(m => m.is_primary)?.marketplace_id
    ?? this._user()?.marketplaces?.[0]?.marketplace_id
    ?? 'US',
  );
  readonly notificationsConfigured = computed(() => this._user()?.notificationsConfigured ?? true);
  readonly notificationChannel = computed(() => this._user()?.notificationChannel ?? 'telegram');
  readonly isTrialing = computed(() => this._user()?.subscriptionStatus === 'trialing');
  readonly trialExpiresAt = computed(() => this._user()?.trialExpiresAt);
  readonly trialDaysRemaining = computed(() => {
    const expiresAt = this._user()?.trialExpiresAt;
    if (expiresAt === undefined) return 0;
    const remaining = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(remaining / 86_400_000));
  });

  // -----------------------------------------------------------------------
  // Tier signals
  // -----------------------------------------------------------------------

  /** Human-readable tier label (Standard / Advanced / Strategic). */
  readonly tierLabel = computed(() => {
    const tier = this.planTier();
    if (!tier) return '';
    return AGENT_TIER_LABELS[extractBaseTier(tier)] ?? '';
  });

  /** Whether the tenant is within a trial period (trial_expires_at is in the future). */
  readonly trialActive = computed(() => {
    const expiresAt = this._user()?.trialExpiresAt;
    if (!expiresAt) return false;
    return new Date(expiresAt) > new Date();
  });

  /**
   * True while the server resolves the tenant's LIMIT tier to a tier other than their own
   * plan (V36, 2026-10-04) — today that means an ACTIVE trial (EC-29). **Derived from the
   * two SERVER-sent tiers (`limit_tier` vs `plan_tier`), deliberately NOT a re-derivation of
   * `isTrialInEffect(status, expiry)`:** the server's rule has a documented date-only
   * fallback for a legacy row with no `subscription_status`, so a client copy would read
   * false there while the server is enforcing Growth's allowance — and the campaigns CTA
   * would then show a paid-tier upgrade line for a tenant the server is limiting to Growth.
   * Comparing the two server answers cannot diverge from `tierService.limitTierOf`.
   */
  readonly trialLimitsActive = computed(() =>
    this.limitTier() !== '' && this.limitTier() !== extractBaseTier(this.planTier()),
  );

  readonly marketplaceCount = computed(() => this.marketplaces().length);

  /** Max marketplaces allowed by the tenant's LIMIT tier (V36 — never the feature tier). */
  readonly marketplaceLimit = computed(() => {
    const tier = this.limitTier();
    if (!tier) return 1;
    return TIER_LIMITS[tier]?.maxMarketplaces ?? 1;
  });

  readonly orderCount = computed(() =>
    this._user()?.config?.monthly_order_count ?? 0,
  );

  /** Max monthly orders allowed by the tenant's LIMIT tier (V36 — never the feature tier). */
  readonly orderLimit = computed(() => {
    const tier = this.limitTier();
    if (!tier) return 300;
    return TIER_LIMITS[tier]?.maxMonthlyOrders ?? 300;
  });

  readonly campaignCount = computed(() =>
    (this._user()?.config?.managed_campaign_ids ?? []).length,
  );

  /** Max managed campaigns allowed by the tenant's LIMIT tier (V36 — never the feature tier). */
  readonly campaignLimit = computed(() => {
    const tier = this.limitTier();
    if (!tier) return 2;
    return TIER_LIMITS[tier]?.maxCampaigns ?? 2;
  });

  /**
   * The lookback vocabularies a tier may select, ascending — mirrors the backend
   * `getLookbackDays(tier)` (`LOOKBACK_DAYS_BY_TIER` in tier.service.ts).
   * A page must offer these and nothing else: the old hardcoded `[1, 3, 7, 14, 30, 60, 90]`
   * intersected the tier lists only at 7/14/30/60/90, which is how a Starter tenant could
   * select a 90-day range the backend then clamped. **Keyed on the LIMIT tier (V36):** the
   * server validates a persisted lookback against the limit tier's vocabulary, so a trial
   * offering Pro's 90 returned a hard 400 naming a tier the seller does not believe they
   * are on.
   */
  readonly lookbackOptions = computed(() => {
    const tier = this.limitTier();
    if (!tier) return FALLBACK_LOOKBACK_DAYS;
    return TIER_LIMITS[tier]?.lookbackDays ?? FALLBACK_LOOKBACK_DAYS;
  });

  /**
   * The range a page opens on: 30 where the tier's vocabulary includes it, else the tier's
   * own largest (Starter opens on 14). `lookbackOptions` is never empty — an unknown tier
   * resolves to the Starter vocabulary — so the range is never taken from nothing.
   */
  readonly lookbackDefault = computed(() => {
    const options = this.lookbackOptions();
    return options.includes(DEFAULT_LOOKBACK_DAYS) ? DEFAULT_LOOKBACK_DAYS : Math.max(...options);
  });

  readonly aiCostUsed = computed(() =>
    this._user()?.config?.monthly_ai_cost_usd ?? 0,
  );

  /**
   * Monthly AI budget in USD for the tenant's LIMIT tier (V36) — the bundled budget is a
   * LIMIT: a trial is capped at Growth's token budget ($9) even though every feature
   * (PRO_*) stays available.
   */
  readonly aiBudget = computed(() => {
    const tier = this.limitTier();
    if (!tier) return AI_BUDGETS['STARTER'];
    return AI_BUDGETS[tier] ?? AI_BUDGETS['STARTER'];
  });

  /** Global budget exhaustion flag from tenant record. */
  readonly budgetExhausted = computed(() =>
    this._user()?.budgetExhausted ?? false,
  );

  /** Whether the platform recommends BYOK due to AI budget constraints. */
  readonly byokRecommended = computed(() =>
    this._user()?.byokRecommended ?? false,
  );

  /**
   * Effective key mode (PMK-default bundled policy 2026-09-08).
   * PMK = platform-managed key; BYOK = seller's own Anthropic key.
   * PMK is bundled on EVERY tier (Starter included) and BYOK is an opt-in at the
   * same flat price. Defaults to PMK (true) while the backend responds — a genuine
   * BYOK tenant flips to false from the stored is_pmk.
   */
  readonly isPmk = computed(() => this._user()?.isPmk ?? true);

  /**
   * Whether the tenant may use PMK. PMK is bundled on every tier (2026-09-08) —
   * Starter included — so this is always true; BYOK remains the opt-in.
   */
  readonly pmkAllowed = computed(() => true);

  /** Human-readable key-mode label for the settings/billing UI. */
  readonly keyModeLabel = computed(() => (this.isPmk() ? 'Platform-managed (PMK)' : 'Your own key (BYOK)'));

  readonly billingCycle = computed(() =>
    this._user()?.config?.billing_cycle ?? 'monthly',
  );

  readonly managedCampaignIds = computed(() =>
    this._user()?.config?.managed_campaign_ids ?? [],
  );

  readonly selectedMarketplaces = computed(() =>
    this._user()?.config?.selected_marketplaces ?? [],
  );

  readonly ordersPaused = computed(() =>
    this._user()?.config?.orders_paused ?? false,
  );

  readonly aiBudgetExhausted = computed(() =>
    this._user()?.config?.ai_budget_exhausted ?? false,
  );

  /** Dashboard usage summary derived from user state. */
  readonly usage = computed(() => ({
    monthly_orders: this._user()?.config?.monthly_order_count ?? 0,
    monthly_fbm_orders: this._user()?.config?.monthly_fbm_order_count ?? 0,
    managed_campaigns: (this._user()?.config?.managed_campaign_ids ?? []).length,
    monthly_ai_cost: this._user()?.config?.monthly_ai_cost_usd ?? 0,
  }));

  // -----------------------------------------------------------------------
  // Session management
  // -----------------------------------------------------------------------

  /**
   * Restore session after page refresh.
   *
   * When running same-origin (production): the HttpOnly ss_token cookie is sent
   * automatically via withCredentials, so a successful /auth/me response confirms
   * the user is authenticated.
   *
   * When running cross-origin (staging / local => remote): the cookie can't be set
   * (Secure + SameSite=Strict vs HTTP localhost). We check sessionStorage for a
   * saved Bearer token and send it as Authorization header. The auth interceptor
   * attaches the header automatically when getToken() is non-null.
   */
  async init(): Promise<void> {
    if (this._initialized()) return;

    try {
      const me = await firstValueFrom(
        this.http.get<GatewayMeResponse>(`${environment.apiUrl}/auth/me`),
      );
      this._user.set(this.mapMeResponse(me));

      // Store access_token for WebSocket / EventSource connections.
      // HttpOnly cookie is not readable by JS, so /auth/me returns a
      // short-lived (2h) bearer token the SPA can pass via query param.
      if (me.access_token !== undefined) {
        this._token.set(me.access_token);
        if (!environment.production) {
          sessionStorage.setItem(this.TOKEN_KEY, me.access_token);
        }
      }
    } catch {
      // No valid session — user stays unauthenticated.
      if (!environment.production) {
        sessionStorage.removeItem(this.TOKEN_KEY);
      }
    } finally {
      this._initialized.set(true);
    }
  }

  login(email: string, password: string): Promise<void> {
    // Step 1: POST /auth/login — backend sets HttpOnly ss_token cookie
    //         AND returns access_token in the JSON response body.
    //         We persist the token to sessionStorage so subsequent requests
    //         (including page refreshes) can use Bearer auth when running
    //         cross-origin (staging from localhost).
    return firstValueFrom(
      this.http.post<GatewayTokenResponse>(`${environment.apiUrl}/auth/login`, { email, password }),
    )
      .then((tokenResponse) => {
        // Keep token in memory (always) + sessionStorage (non-production only)
        this._token.set(tokenResponse.access_token);
        if (!environment.production) {
          sessionStorage.setItem(this.TOKEN_KEY, tokenResponse.access_token);
        }

        // Step 2: GET /auth/me — Bearer header sent by auth interceptor
        //         when getToken() is non-null; cookie sent via withCredentials
        //         as fallback for same-origin (production).
        return firstValueFrom(
          this.http.get<GatewayMeResponse>(`${environment.apiUrl}/auth/me`),
        );
      })
      .then((me) => {
        this._user.set(this.mapMeResponse(me));
        this._initialized.set(true);
        void this.router.navigate(
          me.notifications_configured ? ['/dashboard'] : ['/onboarding'],
        );
      });
  }

  async logout(): Promise<void> {
    // End the credential the browser presents before dropping local state. Without
    // this call the HttpOnly ss_token cookie survives sign-out and the next document
    // load re-authenticates the tenant for the rest of its 24 h lifetime.
    try {
      await firstValueFrom(
        this.http.post<void>(`${environment.apiUrl}/auth/logout`, {}),
      );
    } catch {
      // Best effort: still reset local state if the call fails.
    }
    if (!environment.production) {
      sessionStorage.removeItem(this.TOKEN_KEY);
    }
    this._token.set(null);
    this._user.set(null);
    this._initialized.set(true);
    void this.router.navigate(['/login']);
  }

  markNotificationsConfigured(): void {
    const current = this._user();
    if (current !== null) {
      this._user.set({ ...current, notificationsConfigured: true });
    }
  }

  /** Optimistically update the notification channel after a successful save. */
  updateNotificationChannel(channel: 'telegram' | 'whatsapp'): void {
    const current = this._user();
    if (current === null) return;
    this._user.set({ ...current, notificationChannel: channel });
  }

  /**
   * Optimistically flip the effective key mode after a successful
   * PUT /tenant/key-mode (PMK-default bundled policy 2026-09-08).
   */
  updateKeyMode(keyMode: 'pmk' | 'byok'): void {
    const current = this._user();
    if (current === null) return;
    this._user.set({ ...current, isPmk: keyMode === 'pmk' });
  }

  /** Optimistically update selected marketplaces after a save. */
  updateSelectedMarketplaces(marketplaceIds: string[]): void {
    const current = this._user();
    if (current === null) return;
    this._user.set({
      ...current,
      config: {
        ...current.config,
        selected_marketplaces: marketplaceIds,
      },
    });
  }

  /** Optimistically update the primary marketplace flag after a save. */
  updatePrimaryMarketplace(marketplaceId: string): void {
    const current = this._user();
    if (current === null) return;
    this._user.set({
      ...current,
      marketplaces: current.marketplaces.map((marketplace) => ({
        ...marketplace,
        is_primary: marketplace.marketplace_id === marketplaceId,
      })),
    });
  }

  /** Optimistically append a newly-connected marketplace to the user signal. */
  addConnectedMarketplace(marketplace: MarketplaceInfo): void {
    const current = this._user();
    if (current === null) return;
    const alreadyPresent = current.marketplaces.some(
      (existing) => existing.marketplace_id === marketplace.marketplace_id,
    );
    if (alreadyPresent) return;
    this._user.set({
      ...current,
      marketplaces: [...current.marketplaces, marketplace],
    });
  }

  /** In-memory token for WebSocket onboarding only. Null after page refresh. */
  getToken(): string | null {
    return this._token();
  }

  verifyPayment(
    razorpayPaymentId: string,
    razorpaySubscriptionId: string,
    razorpaySignature: string,
  ): Promise<{ activated: boolean }> {
    return firstValueFrom(
      this.http.post<{ activated: boolean }>(
        `${environment.apiUrl}/auth/verify-payment`,
        {
          razorpay_payment_id: razorpayPaymentId,
          razorpay_subscription_id: razorpaySubscriptionId,
          razorpay_signature: razorpaySignature,
        },
      ),
    );
  }

  // -----------------------------------------------------------------------
  // Usage / limits
  // -----------------------------------------------------------------------

  /**
   * Fetch fresh usage data from the backend.
   * Caller can use the returned TenantUsage or call updateUsage() to sync
   * the user signal with the latest counts.
   */
  async fetchUsage(): Promise<TenantUsage> {
    return firstValueFrom(
      this.http.get<TenantUsage>(`${environment.apiUrl}/tenant/usage`),
    );
  }

  /**
   * Patch the user signal with updated usage counts from fetchUsage().
   * This keeps computed signals like orderCount, aiCostUsed, etc. current.
   */
  updateUsage(usage: TenantUsage): void {
    const current = this._user();
    if (current === null) return;
    this._user.set({
      ...current,
      config: {
        ...current.config,
        monthly_order_count: usage.monthly_order_count,
        monthly_fbm_order_count: usage.monthly_fbm_order_count,
        monthly_ai_cost_usd: usage.monthly_ai_cost_usd,
        ai_budget_exhausted: usage.ai_budget_exhausted,
        orders_paused: usage.orders_paused,
      },
    });
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  private mapMeResponse(me: GatewayMeResponse): SellerUser {
    return {
      tenantId: me.tenant_id,
      email: me.email,
      planTier: me.plan_tier,
      limitTier: me.limit_tier,
      subscriptionStatus: me.subscription_status,
      trialExpiresAt: me.trial_expires_at,
      currency: me.currency,
      marketplaces: (me.marketplaces ?? []).map(m => ({
        marketplace_id: m.marketplace_id,
        country: m.country,
        is_primary: m.is_primary,
      })),
      notificationsConfigured: me.notifications_configured,
      // Single channel only (2026-09-07). A legacy stored 'both' value resolves
      // to 'telegram' (the delivery boundary's normalization) — the auth signal
      // never reports a channel that is no longer settable.
      notificationChannel: me.notification_channel === 'whatsapp' ? 'whatsapp' : 'telegram',
      budgetExhausted: me.budget_exhausted ?? false,
      byokRecommended: me.byok_recommended ?? false,
      // PMK-default bundled policy 2026-09-08 — effective key mode (PMK vs BYOK).
      // PMK is the default on every tier; an absent stored flag resolves to PMK.
      isPmk: me.is_pmk ?? true,
      config: {
        billing_cycle: me.config?.billing_cycle,
        billing_cycle_reset_date: me.config?.billing_cycle_reset_date,
        monthly_order_count: me.config?.monthly_order_count,
        monthly_fbm_order_count: me.config?.monthly_fbm_order_count,
        monthly_ai_cost_usd: me.config?.monthly_ai_cost_usd,
        ai_budget_exhausted: me.config?.ai_budget_exhausted,
        managed_campaign_ids: me.config?.managed_campaign_ids,
        selected_marketplaces: me.config?.selected_marketplaces,
        orders_paused: me.config?.orders_paused,
      },
    };
  }
}
